import type { ViewBackedAppSource } from './types';

export class SourceContractUnavailableError extends Error {
  override readonly name = 'SourceContractUnavailableError';
}

export interface ViewBackedAppSourceWireV1 extends ViewBackedAppSource {
  createdAt: string | null;
  diagnostics: Record<string, unknown> | null;
  presentation: unknown | null;
  presentationError: string | null;
  projectionError: string | null;
  sortOrder: number;
  sourcePresentationContractVersion: 1;
  updatedAt: string | null;
  workspaceId: string;
}

const ROW_KEYS = [
  'accessDocId',
  'appKind',
  'area',
  'blockId',
  'canEdit',
  'canManage',
  'color',
  'createdAt',
  'databaseId',
  'deletedAt',
  'diagnostics',
  'docId',
  'id',
  'lifecycleState',
  'presentation',
  'presentationError',
  'projectionError',
  'scope',
  'sortOrder',
  'sourcePresentationContractVersion',
  'title',
  'trashedAt',
  'updatedAt',
  'viewId',
  'viewMode',
  'visible',
  'workspaceId',
] as const;
const AREA_KEYS = [
  'kind',
  'name',
  'ownerUserId',
  'rootFolderId',
  'systemFolderId',
  'workspaceId',
] as const;

export function decodeViewBackedAppSourcesResponseV1(value: unknown): {
  sources: ViewBackedAppSourceWireV1[];
} {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, ['sources']) ||
    !Array.isArray(value.sources)
  ) {
    unavailable('Invalid view-backed app sources envelope');
  }
  return { sources: value.sources.map(decodeRow) };
}

function decodeRow(value: unknown): ViewBackedAppSourceWireV1 {
  if (!isRecord(value) || !hasExactKeys(value, ROW_KEYS))
    unavailable('Invalid source row keys');
  if (!isRecord(value.area) || !hasExactKeys(value.area, AREA_KEYS))
    unavailable('Invalid source area');
  const area = value.area as Record<string, unknown>;
  const strings = [
    'accessDocId',
    'appKind',
    'blockId',
    'databaseId',
    'docId',
    'id',
    'scope',
    'title',
    'viewId',
    'viewMode',
    'workspaceId',
  ] as const;
  if (strings.some(key => typeof value[key] !== 'string'))
    unavailable('Invalid source string field');
  if (
    ['canEdit', 'canManage', 'visible'].some(
      key => typeof value[key] !== 'boolean'
    )
  )
    unavailable('Invalid source boolean field');
  if (typeof value.sortOrder !== 'number' || !Number.isFinite(value.sortOrder))
    unavailable('Invalid source sort order');
  if (value.sourcePresentationContractVersion !== 1)
    unavailable('Unsupported source presentation contract');
  if (!['active', 'deleted', 'trashed'].includes(String(value.lifecycleState)))
    unavailable('Invalid source lifecycle');
  if (!['private', 'shared'].includes(String(value.scope)))
    unavailable('Invalid source scope');
  for (const key of [
    'color',
    'createdAt',
    'deletedAt',
    'presentationError',
    'projectionError',
    'trashedAt',
    'updatedAt',
  ] as const) {
    if (value[key] !== null && typeof value[key] !== 'string')
      unavailable(`Invalid source ${key}`);
  }
  if (value.diagnostics !== null && !isRecord(value.diagnostics))
    unavailable('Invalid source diagnostics');
  const areaStrings = ['kind', 'name', 'rootFolderId', 'workspaceId'] as const;
  if (areaStrings.some(key => typeof area[key] !== 'string'))
    unavailable('Invalid source area string');
  for (const key of ['ownerUserId', 'systemFolderId'] as const) {
    if (area[key] !== null && typeof area[key] !== 'string')
      unavailable(`Invalid source area ${key}`);
  }
  return value as unknown as ViewBackedAppSourceWireV1;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function hasExactKeys(
  value: Record<string, unknown>,
  keys: readonly string[]
): boolean {
  const actual = Object.keys(value).sort();
  return (
    actual.length === keys.length &&
    [...keys].sort().every((key, index) => actual[index] === key)
  );
}

function unavailable(message: string): never {
  throw new SourceContractUnavailableError(message);
}
